import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions, ApexAxisChartSeries, ApexNonAxisChartSeries } from 'apexcharts';
import { graphql } from '@/gql';
import type { ChartBucket, ChartPreset } from '@/gql/graphql';
import { Card } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { EmptyState } from '@/components/ui/EmptyState';
import { SingleDatePicker } from '@/components/ui/DatePickers';
import { formatCurrency } from '@/lib/format';
import { CHART_COLOR_PALETTE } from './chartPalette';
import { TicketSalesChart, TicketSalesControls } from './TicketSalesChart';
import { useChartsUrlState, type PresetChoice } from './chartsUrlState';

const ChartSeriesDoc = graphql(/* GraphQL */ `
  query ChartSeries(
    $preset: ChartPreset!
    $recognitionDate: String!
    $bucket: ChartBucket!
    $windowStart: String
    $windowEnd: String
  ) {
    chartSeries(
      preset: $preset
      recognitionDate: $recognitionDate
      bucket: $bucket
      windowStart: $windowStart
      windowEnd: $windowEnd
    ) {
      preset
      recognitionDate
      bucket
      availableWindowStart
      availableWindowEnd
      categories
      series {
        name
        data
      }
    }
  }
`);
const ChartAvailableDatesDoc = graphql(/* GraphQL */ `
  query ChartAvailableDates($preset: ChartPreset!) {
    chartAvailableDates(preset: $preset)
  }
`);

const PRESET_META: Record<
  ChartPreset,
  {
    label: string;
    type: 'bar' | 'line' | 'area' | 'pie';
    yCurrency: boolean;
    /** Stack the series (one colour per stage) instead of overlaying them. */
    stacked?: boolean;
  }
> = {
  TOTAL_SALES: { label: 'Total sales', type: 'area', yCurrency: true },
  SALES_PER_DEVICE: { label: 'Sales per device', type: 'line', yCurrency: true },
  TRANSACTIONS_PER_DEVICE: {
    label: 'Transactions per device',
    type: 'line',
    yCurrency: false,
  },
  SALES_PER_CATEGORY: { label: 'Sales per category', type: 'line', yCurrency: true },
  SALES_PER_PAYMENT_METHOD: {
    label: 'Sales per payment method',
    type: 'line',
    yCurrency: true,
  },
  TRANSACTIONS_PER_PAYMENT_METHOD: {
    label: 'Transactions per payment method',
    type: 'line',
    yCurrency: false,
  },
  AVERAGE_TRANSACTION_VALUE: {
    label: 'Average transaction value',
    type: 'area',
    yCurrency: true,
  },
  TIPS: { label: 'Tips', type: 'area', yCurrency: true },
  SALES_PER_PRODUCT: {
    label: 'Sales per product',
    type: 'pie',
    yCurrency: false,
  },
  // One series per sale stage plus Golden ticket: admitted + refused scans.
  ENTRANCE_SCANS: {
    label: 'Entrance scans',
    type: 'bar',
    yCurrency: false,
    stacked: true,
  },
};

const BUCKET_LABELS: Record<ChartBucket, string> = {
  TWO_MIN: '2 minutes',
  FIVE_MIN: '5 minutes',
  TEN_MIN: '10 minutes',
  THIRTY_MIN: '30 minutes',
};

/**
 * Charts page. Replaces the legacy Google Charts Twig templates with a few
 * ApexCharts presets that let operators inspect intra-day performance on a
 * chosen recognition date and time granularity. Every control lives in the
 * query string (`useChartsUrlState`), so a link or a reload reproduces the chart.
 */
export function ChartsPage() {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const { state: urlState, patch } = useChartsUrlState();
  const {
    preset: presetChoice,
    bucket,
    windowStart: selectedWindowStart,
    windowEnd: selectedWindowEnd,
  } = urlState;
  const isTicketSales = presetChoice === 'TICKET_SALES';
  // Ticket sales skips the preset queries below; any real preset stands in so the hooks keep their shape.
  const preset: ChartPreset = presetChoice === 'TICKET_SALES' ? 'TOTAL_SALES' : presetChoice;
  // The day the user picked (the URL's `date`); without one, today, and below the newest day with data.
  const recognitionDate = urlState.date ?? today;

  const { data: availableDatesData, loading: datesLoading } = useQuery(ChartAvailableDatesDoc, {
    skip: isTicketSales,
    variables: { preset },
  });
  const availableDates = useMemo(
    () => availableDatesData?.chartAvailableDates ?? [],
    [availableDatesData],
  );

  const effectiveDate = useMemo(() => {
    if (availableDates.length === 0) return null;
    return availableDates.includes(recognitionDate) ? recognitionDate : availableDates[0];
  }, [availableDates, recognitionDate]);

  const { data, loading: seriesLoading } = useQuery(ChartSeriesDoc, {
    skip: isTicketSales || !effectiveDate,
    variables: {
      preset,
      recognitionDate: effectiveDate ?? '',
      bucket,
      windowStart: selectedWindowStart ?? undefined,
      windowEnd: selectedWindowEnd ?? undefined,
    },
  });

  const chartLoading = datesLoading || !effectiveDate || (seriesLoading && !data?.chartSeries);

  const payload = data?.chartSeries;
  const meta = PRESET_META[preset];
  const isPiePreset = preset === 'SALES_PER_PRODUCT';
  const availableWindowStart = payload?.availableWindowStart ?? null;
  const availableWindowEnd = payload?.availableWindowEnd ?? null;

  const allTimeSlots = useMemo(() => buildBucketSlots(bucket), [bucket]);
  const availableSlots = useMemo(() => {
    if (!availableWindowStart || !availableWindowEnd) return [];
    const startIdx = allTimeSlots.indexOf(availableWindowStart);
    const endIdx = allTimeSlots.indexOf(availableWindowEnd);
    if (startIdx < 0 || endIdx < 0 || startIdx > endIdx) return [];
    return allTimeSlots.slice(startIdx, endIdx + 1);
  }, [allTimeSlots, availableWindowStart, availableWindowEnd]);

  const startOptions = useMemo(() => {
    if (availableSlots.length === 0) return [];
    const endBound = selectedWindowEnd ?? availableSlots[availableSlots.length - 1];
    const endIdx = availableSlots.indexOf(endBound);
    return availableSlots
      .filter((_slot, idx) => idx <= (endIdx >= 0 ? endIdx : availableSlots.length - 1))
      .map((slot) => ({ value: slot, label: slot }));
  }, [availableSlots, selectedWindowEnd]);

  const endOptions = useMemo(() => {
    if (availableSlots.length === 0) return [];
    const startBound = selectedWindowStart ?? availableSlots[0];
    const startIdx = availableSlots.indexOf(startBound);
    return availableSlots
      .filter((_slot, idx) => idx >= (startIdx >= 0 ? startIdx : 0))
      .map((slot) => ({ value: slot, label: slot }));
  }, [availableSlots, selectedWindowStart]);

  const options = useMemo<ApexOptions>(() => {
    if (isPiePreset) {
      return {
        chart: {
          type: 'pie',
          toolbar: { show: false },
          fontFamily: 'inherit',
          animations: { enabled: false },
        },
        labels: payload?.categories ?? [],
        colors: CHART_COLOR_PALETTE,
        legend: { position: 'bottom' },
        dataLabels: { enabled: true },
        tooltip: {
          y: {
            formatter: (v: number) => String(Math.round(v)),
          },
        },
      };
    }

    const categories = payload?.categories ?? [];
    const seriesData = normalizeSeriesData(payload?.series ?? [], categories.length);
    const colors =
      seriesData.length > 0
        ? Array.from({ length: seriesData.length }, (_, idx) => CHART_COLOR_PALETTE[idx % CHART_COLOR_PALETTE.length])
        : [CHART_COLOR_PALETTE[0]];

    return {
      chart: {
        type: meta.type,
        toolbar: { show: false },
        fontFamily: 'inherit',
        stacked: meta.stacked === true,
        zoom: { enabled: true, autoScaleYaxis: true },
        animations: { enabled: false },
        height: 400,
      },
      stroke: { width: meta.type === 'bar' ? 0 : 2, curve: 'smooth' },
      // Keep fill simple; gradient + dynamic series updates can trigger
      // Apex internal color resolution crashes in some builds.
      fill: { opacity: meta.type === 'area' ? 0.35 : 1 },
      dataLabels: { enabled: false },
      xaxis: {
        categories,
        tickAmount: 12,
        labels: { rotate: 0 },
      },
      yaxis: {
        // Scan counts are whole numbers: no 0.5 ticks.
        ...(meta.stacked ? { min: 0, forceNiceScale: true, decimalsInFloat: 0 } : {}),
        labels: {
          formatter: meta.yCurrency
            ? (v: number) => formatCurrency(v)
            : (v: number) => String(Math.round(v)),
        },
      },
      colors,
      // A stage chart names its stage even when only one stage scanned.
      legend: { position: 'top', showForSingleSeries: meta.stacked === true },
      tooltip: {
        x: {
          formatter: (_v: number, opts?: { dataPointIndex?: number }) =>
            (opts?.dataPointIndex !== undefined && categories[opts.dataPointIndex]) || '',
        },
        y: {
          formatter: (v: number) =>
            meta.yCurrency ? formatCurrency(v) : String(Math.round(v)),
        },
      },
    };
  }, [payload, meta, isPiePreset]);
  const chartSeries = useMemo(
    () => {
      if (isPiePreset) {
        const raw = payload?.series?.[0]?.data ?? [];
        return raw.map((v) => (Number.isFinite(v) ? v : 0));
      }

      return normalizeSeriesData(payload?.series ?? [], (payload?.categories ?? []).length);
    },
    [payload, isPiePreset],
  );
  const chartSeriesNames = useMemo(
    () => (payload?.series ?? []).map((s) => s.name).join('|'),
    [payload],
  );
  const chartKey = useMemo(
    () =>
      [
        preset,
        effectiveDate ?? '',
        bucket,
        selectedWindowStart ?? '',
        selectedWindowEnd ?? '',
        payload?.categories?.join('|') ?? '',
        chartSeriesNames,
      ].join('::'),
    [
      preset,
      effectiveDate,
      bucket,
      selectedWindowStart,
      selectedWindowEnd,
      payload?.categories,
      chartSeriesNames,
    ],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Charts
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Intra-day presets and ticket sales per night.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Select
            value={presetChoice}
            onChange={(event) =>
              patch({ preset: event.target.value as PresetChoice, windowStart: null, windowEnd: null })
            }
            options={[
              ...Object.entries(PRESET_META).map(([value, m]) => ({
                value,
                label: m.label,
              })),
              { value: 'TICKET_SALES', label: 'Ticket sales' },
            ]}
            aria-label="Preset"
          />
          {isTicketSales && (
            <TicketSalesControls
              interval={urlState.grouping}
              kind={urlState.type}
              cumulative={urlState.cumulative}
              splitByStage={urlState.split}
              onIntervalChange={(grouping) => patch({ grouping })}
              onKindChange={(type) => patch({ type })}
              onCumulativeChange={(cumulative) => patch({ cumulative })}
              onSplitByStageChange={(split) => patch({ split })}
            />
          )}
          {!isTicketSales && (
            <div className="min-w-[14rem]">
              <SingleDatePicker
                value={effectiveDate ?? recognitionDate}
                onChange={(next) => patch({ date: next, windowStart: null, windowEnd: null })}
                allowedDates={availableDates}
                disabled={datesLoading || availableDates.length === 0}
                ariaLabel="Recognition date"
              />
            </div>
          )}
          {!isTicketSales && !isPiePreset && (
            <>
              <Select
                value={bucket}
                onChange={(event) =>
                  patch({ bucket: event.target.value as ChartBucket, windowStart: null, windowEnd: null })
                }
                options={Object.entries(BUCKET_LABELS).map(([value, label]) => ({
                  value,
                  label,
                }))}
                aria-label="Bucket"
              />
              <Select
                value={selectedWindowStart ?? ''}
                onChange={(event) => patch({ windowStart: event.target.value || null })}
                options={startOptions}
                aria-label="From time"
                disabled={startOptions.length === 0}
              />
              <Select
                value={selectedWindowEnd ?? ''}
                onChange={(event) => patch({ windowEnd: event.target.value || null })}
                options={endOptions}
                aria-label="To time"
                disabled={endOptions.length === 0}
              />
            </>
          )}
        </div>
      </div>

      {isTicketSales ? (
        <TicketSalesChart
          interval={urlState.grouping}
          kind={urlState.type}
          cumulative={urlState.cumulative}
          splitByStage={urlState.split}
          nightIds={urlState.nights}
          onNightsChange={(update) => patch((current) => ({ nights: update(current.nights) }))}
        />
      ) : (
        <Card title={meta.label}>
          {chartLoading ? (
            <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
          ) : (payload?.series.length ?? 0) === 0 ? (
            <EmptyState
              title="No data"
              description="Pick another day or preset — this combination has no measurements."
            />
          ) : (
            <ReactApexChart
              key={chartKey}
              options={options}
              series={chartSeries as ApexAxisChartSeries | ApexNonAxisChartSeries}
              type={meta.type as 'bar' | 'line' | 'area' | 'pie'}
              height={400}
            />
          )}
        </Card>
      )}
    </div>
  );
}

function buildBucketSlots(bucket: ChartBucket): string[] {
  const size = bucket === 'TWO_MIN'
    ? 2
    : bucket === 'FIVE_MIN'
      ? 5
      : bucket === 'TEN_MIN'
        ? 10
        : 30;
  const recognitionStartMinutes = 6 * 60;
  const out: string[] = [];
  for (let offset = 0; offset < 24 * 60; offset += size) {
    const mins = (recognitionStartMinutes + offset) % (24 * 60);
    out.push(`${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`);
  }
  return out;
}

function normalizeSeriesData(
  rawSeries: ReadonlyArray<{ name: string; data: ReadonlyArray<number> }>,
  categoryCount: number,
): Array<{ name: string; data: number[] }> {
  return rawSeries.map((s, idx) => {
    const data = Array.from({ length: categoryCount }, (_unused, pointIdx) => {
      const value = s.data[pointIdx] ?? 0;
      return Number.isFinite(value) ? value : 0;
    });

    return {
      name: s.name || `Series ${idx + 1}`,
      data,
    };
  });
}
